import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { PageHero, CTABand, SectionHead } from "../components/Shared";
import { Reveal } from "../components/Reveal";
import { IMAGES, REAL_PHOTOS } from "../lib/constants";
import { useTitle } from "../lib/useTitle";

const VALUES = [
  { title: "Dignity First", text: "Every woman is treated as a daughter of worth — never a case file, never a number." },
  { title: "Faith as Foundation", text: "Hope anchored in Christ sustains the long, brave work of healing." },
  { title: "Trauma-Informed Care", text: "Safety, choice, and gentleness shape every gathering, meal, and conversation." },
  { title: "Slow, Sustainable Growth", text: "Three unhurried years — because transformation that lasts is grown, not rushed." },
  { title: "Community as Medicine", text: "Sisterhood, shared tables, and shared soil are central to restoration." },
  { title: "Excellence Through Partnership", text: "We collaborate with licensed mental health and community professionals." },
];

export default function About() {
  useTitle("About | Bee Courageous @ Garden Grub");
  return (
    <>
      <PageHero
        eyebrow="About Us"
        lines={["A garden of second chances,", "tended with faith."]}
        lede="Bee Courageous @ Garden Grub began with a simple conviction: women who have endured trauma, loss, or instability deserve more than survival — they deserve restoration."
        image={REAL_PHOTOS.founders}
        imageAlt="Sandra Blum and Meg Harkins, founder and co-founder of Bee Courageous @ Garden Grub"
      />

      {/* Founder's Story */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-12 lg:py-28" data-testid="founder-story">
        <div className="grid items-start gap-12 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <SectionHead
              eyebrow="Our Founder's Story"
              title="Faith, nourishment, and the courage to begin again"
            />
            <Reveal delay={0.12}>
              <div className="mt-6 space-y-5 text-base leading-relaxed text-charcoal/75 sm:text-lg">
                <p className="first-letter:float-left first-letter:mr-3 first-letter:font-serif first-letter:text-6xl first-letter:font-semibold first-letter:leading-[0.85] first-letter:text-wing">
                  Bee Courageous at Garden Grub grew out of my own journey — a journey through hardship, motherhood,
                  faith, and learning that our circumstances do not have to define our future.
                </p>
                <p>
                  As a single mother, I knew what it meant to need help. I experienced life on public assistance and the
                  responsibility of raising a family while working toward greater stability. Those experiences shaped how I
                  see people today. Behind every need is a person with a story, gifts, and possibilities that may simply
                  need room to grow.
                </p>
                <p>
                  In 2019, my son's arrest became a turning point in my life. It brought painful questions and a deeper
                  awareness of how one person's struggles affect an entire family. That season helped shape my desire to
                  walk alongside women and families facing difficult circumstances — to offer compassion, encouragement,
                  and opportunities to move forward.
                </p>
                <p>
                  My faith has been the foundation of that calling. To me, courage means taking the next faithful step,
                  even when the way ahead feels uncertain.
                </p>
                <p>
                  Through Garden Grub, food became a practical way to serve. A nourishing meal can bring people to the
                  table, open a conversation, and create a sense of belonging. Bee Courageous grew from that same desire
                  to care for the whole person: to nourish the body, encourage the heart, and make space for spiritual growth.
                </p>
                <p>
                  Today, Bee Courageous at Garden Grub brings that vision to life through meals, community gatherings,
                  women's retreats, service opportunities, and faith-based programs. Our certified leaders facilitate
                  established curricula that support women as they explore healing and deepen their relationship with God.
                  We recognize that each woman arrives with different experiences, needs, and readiness, and we seek to
                  meet her with dignity and care.
                </p>
                <p>
                  My journey from receiving assistance to founding a nonprofit has given me a conviction I carry into this
                  work: people deserve to be seen for more than the hardest chapter of their lives.
                </p>
                <p>
                  My hope is that every woman who comes through our doors finds a place to belong, discovers her gifts,
                  and gains the courage to take her next step. As she grows, she can also become a source of encouragement
                  and hope for others.
                </p>
              </div>
              <div className="mt-8 flex items-center gap-4">
                <img src={REAL_PHOTOS.sandra} alt="Sandra Blum, founder" className="h-14 w-14 rounded-full border-2 border-pollen/60 object-cover shadow-md" data-testid="founder-portrait" />
                <p className="font-serif text-2xl font-semibold italic text-wing" data-testid="founder-signature">— Sandra Blum, Founder</p>
              </div>
            </Reveal>
          </div>

          <div className="space-y-6 lg:col-span-5">
            <Reveal delay={0.2}>
              <div className="arch-frame overflow-hidden border-4 border-white shadow-2xl shadow-wing/15">
                <img
                  src={REAL_PHOTOS.sandra}
                  alt="Sandra Blum, founder of Bee Courageous at Garden Grub"
                  className="aspect-[4/5] w-full object-cover object-top"
                  loading="lazy"
                  data-testid="founder-arch-portrait"
                />
              </div>
              <p className="mt-3 text-center font-serif text-sm italic text-charcoal/60">
                Sandra Blum, Founder
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <figure className="rounded-3xl border border-pollen/40 bg-pollen-soft p-6 sm:p-8" data-testid="founder-verse">
                <blockquote className="font-serif text-xl font-medium italic leading-snug text-charcoal sm:text-2xl">
                  "Be strong and courageous… for the Lord your God will be with you wherever you go."
                </blockquote>
                <figcaption className="mt-3 text-xs font-bold uppercase tracking-[0.25em] text-pollen-dark">Joshua 1:9 — our anchor verse</figcaption>
              </figure>
            </Reveal>
            <Reveal delay={0.38}>
              <div className="flex flex-wrap gap-3">
                <span className="rounded-full bg-garden-soft px-4 py-2 text-xs font-bold uppercase tracking-widest text-garden" data-testid="founder-chip-warren">Rooted in Warren, PA</span>
                <span className="rounded-full bg-wing-soft px-4 py-2 text-xs font-bold uppercase tracking-widest text-wing" data-testid="founder-chip-year">Founded 2024</span>
                <span className="rounded-full bg-pollen-soft px-4 py-2 text-xs font-bold uppercase tracking-widest text-pollen-dark" data-testid="founder-chip-heart">One meal, one step at a time</span>
              </div>
            </Reveal>
          </div>
        </div>
        <Reveal delay={0.15}>
          <p className="mx-auto mt-16 max-w-3xl text-center font-serif text-2xl font-medium italic leading-snug text-charcoal sm:text-3xl">
            "That is the heart of Bee Courageous: nourishment, belonging, and faith that helps us move forward — one
            person, one meal, and one courageous step at a time."
          </p>
        </Reveal>
      </section>

      {/* Co-Founder's Story */}
      <section className="bg-garden-soft/60 py-20 lg:py-28" data-testid="cofounder-story">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
          <div className="grid items-start gap-12 lg:grid-cols-12">
            <div className="space-y-6 lg:order-2 lg:col-span-5">
              <Reveal delay={0.1}>
                <div className="arch-frame overflow-hidden border-4 border-white shadow-2xl shadow-garden/15">
                  <img
                    src={REAL_PHOTOS.meg}
                    alt="Meg Harkins, co-founder of Bee Courageous at Garden Grub, smiling in a garden"
                    className="aspect-[4/5] w-full object-cover"
                    loading="lazy"
                    data-testid="cofounder-portrait"
                  />
                </div>
                <p className="mt-3 text-center font-serif text-sm italic text-charcoal/60">Meg Harkins, Co-Founder</p>
              </Reveal>
              <Reveal delay={0.2}>
                <figure className="rounded-3xl border border-garden/25 bg-white p-6 shadow-sm sm:p-8" data-testid="cofounder-quote">
                  <blockquote className="font-serif text-2xl font-medium italic leading-snug text-garden sm:text-3xl">
                    "My story wasn't over. And neither is yours."
                  </blockquote>
                  <figcaption className="mt-3 text-xs font-bold uppercase tracking-[0.25em] text-garden/70">Meg Harkins — Co-Founder</figcaption>
                </figure>
              </Reveal>
              <Reveal delay={0.3}>
                <div className="flex flex-wrap gap-3">
                  <span className="rounded-full bg-white px-4 py-2 text-xs font-bold uppercase tracking-widest text-garden shadow-sm" data-testid="cofounder-chip-nursing">25+ Years in Nursing</span>
                  <span className="rounded-full bg-white px-4 py-2 text-xs font-bold uppercase tracking-widest text-wing shadow-sm" data-testid="cofounder-chip-whole">Body · Soul · Spirit</span>
                  <span className="rounded-full bg-white px-4 py-2 text-xs font-bold uppercase tracking-widest text-pollen-dark shadow-sm" data-testid="cofounder-chip-grace">Grace Upon Grace</span>
                </div>
              </Reveal>
            </div>
            <div className="lg:order-1 lg:col-span-7">
              <SectionHead eyebrow="Our Co-Founder's Story" title="From survival to surrender" />
              <Reveal delay={0.12}>
                <div className="mt-6 space-y-5 text-base leading-relaxed text-charcoal/75 sm:text-lg">
                  <p className="first-letter:float-left first-letter:mr-3 first-letter:font-serif first-letter:text-6xl first-letter:font-semibold first-letter:leading-[0.85] first-letter:text-garden">
                    My story includes abuse, alcoholism and addiction, broken relationships, life-threatening illness,
                    and choices I wish I could make differently. There were seasons when I lived in survival mode and
                    allowed my past to shape how I saw myself and the choices I made.
                  </p>
                  <p className="font-serif text-xl font-semibold italic text-garden sm:text-2xl">
                    But God had a different story to tell.
                  </p>
                  <p>
                    Through surrendering my life to Jesus, I began to understand that healing wasn't about erasing my
                    past. It was allowing God to redeem it. I learned to receive His grace, forgive myself and others,
                    break unhealthy patterns, and discover who I am in Christ.
                  </p>
                  <p>
                    My more than 25 years in nursing also shaped the way I see healing. We are whole people. What affects
                    the body can affect the mind and spirit. What wounds the heart can show up in relationships, habits,
                    health, and the way we see ourselves. That is why I am passionate about helping women pursue healing
                    in body, soul, and spirit.
                  </p>
                  <p>
                    I don't walk beside women because I have always gotten it right. I walk beside them because I know
                    what it is to need grace, another chance, and the courage to begin again.
                  </p>
                  <p>
                    I believe Jesus still restores. I believe cycles can be broken. And I believe the places we once felt
                    most disqualified can become the very places God uses to help someone else.
                  </p>
                </div>
                <p className="mt-8 font-serif text-2xl font-semibold italic text-garden" data-testid="cofounder-signature">— Meg Harkins, Co-Founder</p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-12 lg:py-28">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <SectionHead eyebrow="Who We Serve" title="For the woman starting over" />
          </div>
          <div className="lg:col-span-7">
            <Reveal>
              <div className="space-y-5 text-base leading-relaxed text-charcoal/75 sm:text-lg">
                <p>
                  We serve women who have experienced trauma, loss, homelessness, domestic instability, addiction recovery,
                  incarceration re-entry, or the quiet erosion of hope that comes from years of simply surviving.
                </p>
                <p>
                  Our answer is not a single program but a whole ecosystem: faith-based empowerment, holistic health coaching,
                  nourishment through food, gardening for sustainability, vocational training, and a community that refuses to
                  let go. Over three years, women move from foundation, to growth, to leadership.
                </p>
                <p>
                  The bee in our name is our teacher — small but courageous, gathering what is good, building something sweet
                  that nourishes everyone around it. That is what our women do, season after season.
                </p>
              </div>
              <Link to="/program" className="btn-primary mt-8" data-testid="about-program-btn">
                Discover the Three-Year Journey <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-20 sm:px-6 lg:px-12 lg:pb-28" data-testid="merger-story">
        <div className="grid items-center gap-10 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <div className="overflow-hidden rounded-3xl border-4 border-white bg-white shadow-2xl shadow-wing/15">
              <img src={REAL_PHOTOS.merger} alt="Two Hearts, One Mission — the story of Bee Courageous and Garden Grub joining as one ministry" className="w-full object-contain" loading="lazy" />
            </div>
          </Reveal>
          <div className="lg:col-span-7">
            <SectionHead eyebrow="Two Hearts, One Mission" title="One greater impact" />
            <Reveal delay={0.15}>
              <p className="mt-4 text-base leading-relaxed text-charcoal/75 sm:text-lg">
                Bee Courageous and Garden Grub began as two separate ministries — one healing hearts through faith,
                one nourishing bodies through food. We've merged them into one transformational space where women and
                our community are nourished in body, soul, and spirit.
              </p>
              <p className="mt-4 font-serif text-xl italic leading-snug text-wing sm:text-2xl">
                "Meals feed bodies. Community restores hope. Purpose changes lives."
              </p>
              <p className="mt-4 text-base leading-relaxed text-charcoal/75 sm:text-lg">
                Many of the women in our healing programs gain confidence, purpose, and practical skills by serving in
                the garden and kitchen — and friends of Garden Grub often discover a healing journey waiting for them too.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-wing-soft/50 py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
          <SectionHead center eyebrow="What We Hold Dear" title="Values that keep the garden growing" />
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3 lg:gap-8">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={0.07 * i}>
                <article className="card-soft h-full" data-testid={`value-${i}`}>
                  <h3 className="font-serif text-xl font-semibold text-wing sm:text-2xl">{v.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-charcoal/70 sm:text-base">{v.text}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTABand
        title="Come see what is growing here."
        text="Join us for a meal, a garden day, or a conversation. The gate is open."
        primaryLabel="Visit or Volunteer"
      />
    </>
  );
}
